import { CMTagData, useCMTagList } from "api/api";
import LoadingOverlay from "components/basic/LoadingOverlay";
import Card from "components/cards/Card";
import { motion } from "framer-motion";
import { Energy_Pile, TimeOption } from "model/Datatypes";
import React, { useEffect, useMemo, useState } from "react";
import Icon from "resources/icons/Icon";
import getAverage from "utility/getAverage";
import { hourTimeOption, limitedYarTimeOption, stdTimeOptions } from "utility/timeOptions";
import "./piles.css";

type IEnergyPiles = {
  tags: Energy_Pile["tags"];
  metaData: Energy_Pile["metaData"];
  pileTags: Energy_Pile["pileTags"];
};
const FONT_SIZE = "14px";

export const EnergyPiles = ({ metaData, pileTags }: IEnergyPiles) => {
  const [time, setTime] = useState<TimeOption>(hourTimeOption);
  const [pylonData, setPylonData] = useState<
    {
      power: CMTagData | undefined;
      returnTemp: CMTagData | undefined;
      supplyTemp: CMTagData | undefined;
      coilNumber: string;
      avgReturnTemp: number | null;
      avgSupplyTemp: number | null;
      avgPower: number | null;
    }[]
  >([]);

  const pylonTags = useMemo(() => {
    if (pileTags === undefined) return [];
    return [
      ...pileTags.map((p) => p.power),
      ...pileTags.map((p) => p.returnTemp),
      ...pileTags.map((p) => p.supplyTemp),
    ];
  }, [pileTags]);

  const { cmData, loading } = useCMTagList(
    pylonTags,
    time.from,
    time.to,
    time.groupingsType,
    true
  );

  // Find the data from the correct tags and put them into variables that describe the data
  useEffect(() => {
    if (cmData && pileTags) {
      const pilonData = pileTags.map((p) => {
        const power = cmData.find((data) => data.name === p.power);
        const returnTemp = cmData.find((data) => data.name === p.returnTemp);
        const supplyTemp = cmData.find((data) => data.name === p.supplyTemp);
        const coilNumber = p.coilNumber;

        const allPowerValues = power?.points.map((point) => point.value);
        const avgPower = allPowerValues ? getAverage(allPowerValues) : null;

        const allSupplyTemp = supplyTemp?.points.map((point) => point.value);
        const avgSupplyTemp = allSupplyTemp ? getAverage(allSupplyTemp) : null;

        const allReturnTemp = returnTemp?.points.map((point) => point.value);
        const avgReturnTemp = allReturnTemp ? getAverage(allReturnTemp) : null;

        return {
          power,
          returnTemp,
          supplyTemp,
          coilNumber,
          avgPower,
          avgReturnTemp,
          avgSupplyTemp,
        };
      });
      setPylonData(pilonData);
    } else {
      setPylonData([]);
    }
  }, [cmData, pileTags]);

  return (
    <div className="w-full h-full relative">
      {loading ? (
        <LoadingOverlay />
      ) : (
        <>
          <div className="flex">
            <div className="font-medium absolute top-4 left-4 2xl:top-10 2xl:left-10 text-base 2xl:text-5xl z-10 text-gray-600">
              Energy Piles
            </div>
            <div className="flex w-full pt-2 rounded justify-end absolute top-0 right-32 2xl:right-64 2xl:top-2 z-20">
              <div className="bg-white px-2 2xl:p-6 rounded-sm shadow-sm mr-4 font-normal text-xs 2xl:text-2xl text-gray-600 border border-gray-300">
                <div className="p-2 px-4 text-xs  2xl:text-4xl font-medium">
                  Number of energy piles
                </div>
                <div className="pb-1 px-4 center">Per group: {metaData.pylonsPerGroup}</div>
                <div className="py-1 px-4">Total amount: {metaData.totalAmountOfPylons}</div>
                <div className="py-1 pb-2 px-4">Total length: {metaData.totalLength}</div>
              </div>
            </div>
          </div>
          <div className="absolute w-full h-full bottom-0">
            <Card
              timeOptions={[...stdTimeOptions, limitedYarTimeOption]}
              timeSelected={time}
              onNewTimeSelected={(newTime) => setTime(newTime)}
              size="full"
              loading={loading}
              flipdropdown={true}
            >
              <div
                // style={{ gridGap: "40px" }}
                className="h-full 2xl:px-28 px-14 z-10 w-full grid grid-cols-6 justify-items-end items-center gap-0 relative"
              >
                {pylonData.map((pd) => {
                  return pd === pylonData[0] ? (
                    <div
                      key={pd.coilNumber}
                      className="z-40 transform 2xl:h-1/2 2xl:w-full scale-125 2xl:scale-150"
                    >
                      <MainPile
                        avgPower={pd.avgPower}
                        avgReturnTemp={pd.avgReturnTemp}
                        avgSupplyTemp={pd.avgSupplyTemp}
                        coilNumber={pd.coilNumber}
                      />
                    </div>
                  ) : (
                    <div
                      key={pd.coilNumber}
                      className={`z-0 2xl:h-full 2xl:w-full 2xl:scale-90 scale-75  transform`}
                    >
                      <SecondaryPile
                        avgPower={pd.avgPower}
                        avgReturnTemp={pd.avgReturnTemp}
                        avgSupplyTemp={pd.avgSupplyTemp}
                        coilNumber={pd.coilNumber}
                      />
                    </div>
                  );
                })}
              </div>

              <div
                className="w-full absolute top-0 left-0 z-0 h-20 2xl:h-1/6"
                style={{
                  // height: "100px",
                  background:
                    "linear-gradient(180deg, #80B7CE 0%, rgba(98, 219, 245, 0) 100%)",
                }}
              ></div>
              <div
                className="w-full absolute bottom-0 left-0 z-0 h-96 2xl:h-7/12 lg:h-7/12"
                style={{
                  // height: "380px",
                  background:
                    "linear-gradient(180deg, rgba(187,172,152,1) 0%, rgba(187,172,152,0) 100%)",
                }}
              ></div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

type PileData = {
  coilNumber: string;
  avgReturnTemp: number | null;
  avgSupplyTemp: number | null;
  avgPower: number | null;
};

const MainPile = ({ coilNumber, avgPower, avgReturnTemp, avgSupplyTemp }: PileData) => {
  return (
    <>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        fill="none"
        viewBox="0 0 229 610"
      >
        <g id="Frame 2796">
          <g id="Group 2938">
            <g id="mainpile">
              <g id="outputTemperature">
                <text
                  fill={avgReturnTemp! > avgSupplyTemp! ? "#4EA6FF" : "#FF5F45"}
                  className="font-normal"
                  transform="translate(5, 110)"
                  fontSize={FONT_SIZE}
                >
                  <tspan x="0" y="13">
                    {avgSupplyTemp?.toFixed(1) + "°C"}
                  </tspan>
                </text>
              </g>
              <g id="input Temperature">
                <text
                  fill={avgReturnTemp! < avgSupplyTemp! ? "#4EA6FF" : "#FF5F45"}
                  className="font-normal"
                  transform="translate(2, 172)"
                  fontSize={FONT_SIZE}
                >
                  <tspan x="0" y="13">
                    {avgReturnTemp?.toFixed(1) + "°C"}
                  </tspan>
                </text>
              </g>
              <g id="rectangles">
                <g id="pile">
                  <text
                    fill="#9F9F9F"
                    className="font-medium text-sm"
                    transform="translate(52, 585)"
                  >
                    <tspan x="0" y="13">
                      {"Pile group " + coilNumber}
                    </tspan>
                  </text>
                  <g id="middleBox" opacity="0.3">
                    <path
                      id="Vector 263"
                      stroke="#A0A0A0"
                      d="M77 20.816L122.5 0 168 20.816"
                    ></path>
                    <path
                      id="Vector 262"
                      stroke="#A0A0A0"
                      d="M77 20.816l45.5 20.816L168 20.816"
                    ></path>
                    <path id="Vector 261" stroke="#A0A0A0" d="M122.5 41.632V98.083"></path>
                    <path id="Vector 264" stroke="#A0A0A0" d="M168 20.816v76.35"></path>
                    <path
                      id="Vector 266"
                      stroke="#A0A0A0"
                      d="M77 21.083v68.648l46 3.769"
                    ></path>
                    <path
                      id="overlayShadow"
                      fill="url(#paint0_linear)"
                      d="M83.383 42H186.514V100H83.383z"
                    ></path>
                  </g>
                  <g id="middleBox_2" opacity="0.6">
                    <path
                      id="Vector 263_2"
                      stroke="#A0A0A0"
                      d="M60 77.816L105.5 57 151 77.816"
                    ></path>
                    <path
                      id="Vector 262_2"
                      stroke="#A0A0A0"
                      d="M60 77.816l45.5 20.816L151 77.816"
                    ></path>
                    <path id="Vector 261_2" stroke="#A0A0A0" d="M105.5 98.632V155.083"></path>
                    <path id="Vector 264_2" stroke="#A0A0A0" d="M151 77.816v76.351"></path>
                    <path
                      id="Vector 266_2"
                      stroke="#A0A0A0"
                      d="M60 78.083v68.648l46 3.769"
                    ></path>
                    <path
                      id="overlayShadow_2"
                      fill="url(#paint1_linear)"
                      d="M64.731 99H167.862V157H64.731z"
                    ></path>
                  </g>
                  <g id="Vector">
                    <path
                      fill="#fff"
                      d="M133 524.046L87.5 547 42 525.281V129.719L87.5 108l45.5 21.719v394.327z"
                    ></path>
                    <path
                      stroke="#A0A0A0"
                      d="M133 524.046L87.5 547 42 525.281V129.719m0 0L87.5 108l45.5 21.719-45.5 21.719M42 129.719l45.5 21.719m0 0v395.1M133 129.5v395"
                    ></path>
                  </g>
                </g>
              </g>
              <g id="lines" stroke="#797979">
                <path
                  id="lineToFollowBack"
                  strokeDasharray="8 2"
                  d="M115.5 72l-10-44.5"
                ></path>
                <path
                  id="lineToFollowFront"
                  strokeDasharray="8 2"
                  d="M17 167.5L75.5 136v382l26-11.5v-382L89.5 85"
                ></path>
                <motion.path
                  style={{
                    display: "block",
                    offsetPath: "M17 167.5L75.5 136v382l26-11.5v-382L89.5 85",
                  }}
                  id="arrow"
                  transition={{ duration: 2, yoyo: Infinity }}
                  initial={{ offsetDistance: 0 }}
                  animate={{ offsetDistance: 1 }}
                  d="M47.5 146l6.5 1.5-1.5 6.5"
                ></motion.path>
                <path id="arrow_2" d="M84 75l-6 2.5-2-6"></path>
                <path id="arrow_3" d="M39.5 148l-6.5-1.5 1.5-6.5"></path>
                <path id="arrow_4" d="M80.143 226.146l-4.68 4.735-4.698-4.735"></path>
                <path id="arrow_5" d="M80.178 447.23l-4.68 4.735-4.698-4.735"></path>
                <path id="arrow_6" d="M96.982 326.598l4.482-4.922 4.436 4.922"></path>
                <path
                  id="lineToFollowGoingBack"
                  strokeDasharray="8 2"
                  d="M11 157.5L61.5 132l36-117.5"
                ></path>
                <path id="arrow_7" d="M92 106l2-5.5 5 3"></path>
                <path id="arrow_8" d="M106 46l2.5-5 4.5 3.5"></path>
              </g>
              <g transform="translate(-18)" id="kw">
                <g id="energy">
                  <g id="Group 2881">
                    <g id="Group 2735">
                      <g id="Group 2734">
                        <g id="Group 2714" transform="translate(173, 330) rotate(90)">
                          <g transform="translate(-25, -2)" width="5px">
                            <Icon
                              type={
                                avgReturnTemp! > avgSupplyTemp!
                                  ? "coolingPiles"
                                  : "heatingPiles"
                              }
                            />
                          </g>
                          <text
                            fill="#797979"
                            className="text-sm font-light"
                            fontSize={FONT_SIZE}
                          >
                            <tspan x="0" y="13">
                              {avgPower?.toFixed(1) + "kW"}
                            </tspan>
                          </text>
                        </g>
                      </g>
                    </g>
                  </g>
                </g>
              </g>
            </g>
          </g>
        </g>
        <defs>
          <filter
            id="filter0_d"
            width="27.36"
            height="20"
            x="148.697"
            y="314"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
            <feColorMatrix
              in="SourceAlpha"
              result="hardAlpha"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            ></feColorMatrix>
            <feOffset></feOffset>
            <feGaussianBlur stdDeviation="3"></feGaussianBlur>
            <feColorMatrix values="0 0 0 0 0.184314 0 0 0 0 0.482353 0 0 0 0 0.823529 0 0 0 0.302 0"></feColorMatrix>
            <feBlend in2="BackgroundImageFix" result="effect1_dropShadow"></feBlend>
            <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"></feBlend>
          </filter>
          <linearGradient
            id="paint0_linear"
            x1="134.949"
            x2="134.949"
            y1="42"
            y2="100"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fff" stopOpacity="0"></stop>
            <stop offset="0.224" stopColor="#fff"></stop>
          </linearGradient>
          <linearGradient
            id="paint1_linear"
            x1="116.297"
            x2="116.297"
            y1="99"
            y2="157"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#fff" stopOpacity="0"></stop>
            <stop offset="0.224" stopColor="#fff"></stop>
          </linearGradient>
        </defs>
      </svg>
      {/* <motion.div
        id="pilesAnimation"
        style={{ offsetPath: "M17 167.5L75.5 136v382l26-11.5v-382L89.5 85" }}
        className="w-20 top-0 left-0 absolute h-20 bg-red-700"
        initial={{ offsetDistance: "0%" }}
        animate={{ offsetDistance: "100%" }}
        transition={{ duration: 5, repeat: Infinity }}
      >
        Hello
      </motion.div> */}
    </>
  );
};

<motion.path
  style={{
    display: "block",
    offsetPath: "M17 167.5L75.5 136v382l26-11.5v-382L89.5 85",
  }}
  id="arrow"
  transition={{ duration: 2, yoyo: Infinity }}
  initial={{ offsetDistance: 0 }}
  animate={{ offsetDistance: 1 }}
  d="M47.5 146l6.5 1.5-1.5 6.5"
></motion.path>;

const SecondaryPile = ({ coilNumber, avgPower, avgReturnTemp, avgSupplyTemp }: PileData) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      fill="none"
      viewBox="0 0 142 611"
    >
      <g id="secondaryPile">
        <text fill="#9F9F9F" className="font-medium text-sm" transform="translate(23, 595)">
          <tspan x="0" y="13">
            {"Pile group " + coilNumber}
          </tspan>
        </text>
        <g id="backBox" opacity="0.3">
          <path id="Vector 263" stroke="#A0A0A0" d="M49 22.415L94.5 1 140 22.415"></path>
          <path id="Vector 262" stroke="#A0A0A0" d="M49 22.415L94.5 43.83 140 22.415"></path>
          <path id="Vector 261" stroke="#A0A0A0" d="M94.5 43.83V101.906"></path>
          <path id="Vector 264" stroke="#A0A0A0" d="M140 22.415v78.548"></path>
          <path id="Vector 266" stroke="#A0A0A0" d="M49 22.69v70.623l46 3.878"></path>
          <path
            id="overlayShadow"
            fill="url(#paint0_linear)"
            d="M48 45.323H142V102.849H48z"
          ></path>
        </g>
        <g id="middleBox" opacity="0.6">
          <path
            id="Vector 263_2"
            stroke="#A0A0A0"
            d="M31 79.454l45.5-21.415L122 79.454"
          ></path>
          <path
            id="Vector 262_2"
            stroke="#A0A0A0"
            d="M31 79.454l45.5 21.415L122 79.454"
          ></path>
          <path id="Vector 261_2" stroke="#A0A0A0" d="M76.5 100.869v58.076"></path>
          <path id="Vector 264_2" stroke="#A0A0A0" d="M122 79.454v78.548"></path>
          <path id="Vector 266_2" stroke="#A0A0A0" d="M31 79.729v70.623l46 3.877"></path>
          <path
            id="overlayShadow_2"
            fill="url(#paint1_linear)"
            d="M30 107.077H124V159.888H30z"
          ></path>
        </g>
        <g id="Vector">
          <path
            fill="#fff"
            d="M104 525.303l-45.5 22.633L13 526.521V136.492l45.5-21.415 45.5 21.415v388.811z"
          ></path>
          <path
            stroke="#A0A0A0"
            d="M104 525.303l-45.5 22.633L13 526.521V136.492m0 0l45.5-21.415 45.5 21.415m-91 0l45.5 21.415m45.5-21.415l-45.5 21.415m45.5-21.415V525.5M58.5 157.907v389.574"
          ></path>
        </g>
        <g id="inputAndOutputTemperaturePile2" transform="translate(4)">
          <g id="Group 2920">
            <g id="Group 2919">
              <path
                id="Arrow 6"
                fill={avgReturnTemp! > avgSupplyTemp! ? "#4EA6FF" : "#FF5F45"}
                d="M3.283 567.717a.4.4 0 00-.566 0l-2.545 2.546a.4.4 0 10.565.565L3 568.566l2.263 2.262a.4.4 0 10.566-.565l-2.546-2.546zM3.4 579v-11h-.8v11h.8z"
              ></path>
              <g id="Group 2710">
                <text
                  fill={avgReturnTemp! > avgSupplyTemp! ? "#4EA6FF" : "#FF5F45"}
                  className="font-normal"
                  transform="translate(13,565)"
                  fontSize={FONT_SIZE}
                >
                  <tspan x="0" y="13">
                    {avgSupplyTemp?.toFixed(1) + "°C"}
                  </tspan>
                </text>
              </g>
            </g>
            <g id="Group 2918" fill="#FF5F45">
              <text
                fill={avgReturnTemp! < avgSupplyTemp! ? "#4EA6FF" : "#FF5F45"}
                className="font-normal"
                transform="translate(70,565)"
                fontSize={FONT_SIZE}
              >
                <tspan x="0" y="13">
                  {avgReturnTemp?.toFixed(1) + "°C"}
                </tspan>
              </text>
              <path
                id="Arrow 5"
                fill={avgReturnTemp! < avgSupplyTemp! ? "#4EA6FF" : "#FF5F45"}
                d="M62.717 579.283a.4.4 0 00.566 0l2.545-2.546a.4.4 0 10-.565-.565L63 578.434l-2.263-2.262a.4.4 0 10-.566.565l2.546 2.546zM62.6 568v11h.8v-11h-.8z"
              ></path>
            </g>
          </g>
        </g>
        <g id="energyPile2" transform="translate(129, 306) rotate(90)">
          <Icon type={avgReturnTemp! > avgSupplyTemp! ? "coolingPiles" : "heatingPiles"} />
          <text fill="#797979" className="text-sm font-light" fontSize={FONT_SIZE}>
            <tspan x="24" y="16">
              {avgPower?.toFixed(1) + "kW"}
            </tspan>
          </text>
        </g>
      </g>
      <defs>
        <filter
          id="filter0_d"
          width="26"
          height="20"
          x="103"
          y="315"
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix"></feFlood>
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          ></feColorMatrix>
          <feOffset></feOffset>
          <feGaussianBlur stdDeviation="3"></feGaussianBlur>
          <feColorMatrix values="0 0 0 0 0.184314 0 0 0 0 0.482353 0 0 0 0 0.823529 0 0 0 0.302 0"></feColorMatrix>
          <feBlend in2="BackgroundImageFix" result="effect1_dropShadow"></feBlend>
          <feBlend in="SourceGraphic" in2="effect1_dropShadow" result="shape"></feBlend>
        </filter>
        <linearGradient
          id="paint0_linear"
          x1="95"
          x2="95"
          y1="45.323"
          y2="102.849"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff" stopOpacity="0"></stop>
          <stop offset="0.224" stopColor="#fff"></stop>
        </linearGradient>
        <linearGradient
          id="paint1_linear"
          x1="77"
          x2="77"
          y1="107.077"
          y2="159.888"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#fff" stopOpacity="0"></stop>
          <stop offset="0.224" stopColor="#fff"></stop>
        </linearGradient>
      </defs>
    </svg>
  );
};
